import { atom } from 'nanostores';
import { ConfigDatabase } from '../../engine/database';
import { craftsData, type CraftConfig } from '../../generated/crafts';
import { profileStore, profileActions } from '../../stores/game/profile';
import { gameEventBus } from '../../game/events';

export type CraftId = string;

/**
 * Интерфейс для стоимости крафта
 */
export interface CraftCost {
  inputItem: string;
  inputQuantity: number;
  outputItem: string;
  outputQuantity: number;
  timeSeconds: number;
  station: string;
  requiredTier: number;
}

/**
 * Интерфейс для информации о крафте
 */
export interface CraftInfo {
  craftId: CraftId;
  config: CraftConfig;
  cost: CraftCost;
  canCraft: boolean;
  isUnlocked: boolean;
  currentInputAmount: number;
  currentOutputAmount: number;
}

/**
 * Интерфейс для активного крафта
 */
export interface ActiveCraft {
  craftId: CraftId;
  startTime: number;
  endTime: number;
  quantity: number;
  station: string;
}

/**
 * База данных для крафтинга
 */
export class CraftDB extends ConfigDatabase<CraftConfig, string> {
  private activeCrafts = atom<ActiveCraft[]>([]);
  private playerLevel = atom<number>(1);

  constructor() {
    super({
      base: craftsData,
      overrides: {},
      getId: config => config.recipe_id,
      validate: (config, id) => {
        if (config.input_qty < 1) {
          throw new Error(`Craft ${id}: input_qty must be >= 1`);
        }
        if (config.out_qty < 1) {
          throw new Error(`Craft ${id}: out_qty must be >= 1`);
        }
        if (config.base_time_s < 0) {
          throw new Error(`Craft ${id}: base_time_s must be >= 0`);
        }
        if (config.req_tier < 1) {
          throw new Error(`Craft ${id}: req_tier must be >= 1`);
        }
      },
    });

    this.startCraftTimer();
  }

  /**
   * Инициализация базы данных
   */
  init(): void {
    super.init();
  }

  /**
   * Настройка подписки на уровень игрока (ленивая)
   */
  private setupPlayerLevelSubscription(): void {
    if (this.playerLevelSubscription) return; // Уже настроена

    this.playerLevelSubscription = profileStore.subscribe(profile => {
      this.playerLevel.set(profile.gameData.level);
    });
  }

  private playerLevelSubscription: (() => void) | null = null;

  /**
   * Запуск таймера для обработки завершенных крафтов
   */
  private startCraftTimer(): void {
    setInterval(() => {
      this.processCompletedCrafts();
    }, 1000); // Проверяем каждую секунду
  }

  /**
   * Обработка завершенных крафтов
   */
  private processCompletedCrafts(): void {
    const now = Date.now();
    const currentCrafts = this.activeCrafts.get();
    const completedCrafts: ActiveCraft[] = [];
    const remainingCrafts: ActiveCraft[] = [];

    for (const craft of currentCrafts) {
      if (now >= craft.endTime) {
        completedCrafts.push(craft);
      } else {
        remainingCrafts.push(craft);
      }
    }

    if (completedCrafts.length > 0) {
      // Выдаем результаты крафта
      for (const craft of completedCrafts) {
        const config = this.getCraftConfig(craft.craftId);
        if (config) {
          const totalOutput = config.out_qty * craft.quantity;
          profileActions.addInventoryItem(config.output_item, totalOutput);

          // Отправляем событие о завершении крафта
          gameEventBus.emit('craft-completed', {
            craftId: craft.craftId,
            quantity: craft.quantity,
          });
        }
      }

      // Обновляем список активных крафтов
      this.activeCrafts.set(remainingCrafts);
    }
  }

  /**
   * Получить конфигурацию крафта
   */
  getCraftConfig(craftId: CraftId): CraftConfig | undefined {
    return this.get(craftId);
  }

  /**
   * Получить стоимость крафта
   */
  getCraftCost(craftId: CraftId): CraftCost | null {
    const config = this.getCraftConfig(craftId);
    if (!config) return null;

    return {
      inputItem: config.input_item,
      inputQuantity: config.input_qty,
      outputItem: config.output_item,
      outputQuantity: config.out_qty,
      timeSeconds: config.base_time_s,
      station: config.station,
      requiredTier: config.req_tier,
    };
  }

  /**
   * Проверить, разблокирован ли крафт
   */
  isCraftUnlocked(craftId: CraftId): boolean {
    this.setupPlayerLevelSubscription(); // Ленивая инициализация
    const config = this.getCraftConfig(craftId);
    if (!config) return false;

    const playerLevel = this.playerLevel.get();
    return playerLevel >= config.req_tier;
  }

  /**
   * Проверить, может ли игрок крафтить
   */
  canCraft(craftId: CraftId, quantity: number = 1): boolean {
    const config = this.getCraftConfig(craftId);
    if (!config || !this.isCraftUnlocked(craftId)) return false;

    const requiredInput = config.input_qty * quantity;
    const currentInput = profileActions.getInventoryItemQuantity(config.input_item);

    return currentInput >= requiredInput;
  }

  /**
   * Получить полную информацию о крафте
   */
  getCraftInfo(craftId: CraftId): CraftInfo | null {
    const config = this.getCraftConfig(craftId);
    if (!config) return null;

    const cost = this.getCraftCost(craftId);
    if (!cost) return null;

    const currentInputAmount = profileActions.getInventoryItemQuantity(cost.inputItem);
    const currentOutputAmount = profileActions.getInventoryItemQuantity(cost.outputItem);

    return {
      craftId,
      config,
      cost,
      canCraft: this.canCraft(craftId),
      isUnlocked: this.isCraftUnlocked(craftId),
      currentInputAmount,
      currentOutputAmount,
    };
  }

  /**
   * Начать крафт
   */
  startCraft(craftId: CraftId, quantity: number = 1): boolean {
    if (!this.canCraft(craftId, quantity)) return false;

    const config = this.getCraftConfig(craftId);
    if (!config) return false;

    // Списываем ресурсы
    const requiredInput = config.input_qty * quantity;
    profileActions.removeInventoryItem(config.input_item, requiredInput);

    // Если время крафта 0 - выполняем мгновенно
    if (config.base_time_s <= 0) {
      const totalOutput = config.out_qty * quantity;
      profileActions.addInventoryItem(config.output_item, totalOutput);

      // Отправляем события о начале и завершении крафта
      gameEventBus.emit('craft-started', {
        craftId,
        quantity,
      });

      gameEventBus.emit('craft-completed', {
        craftId,
        quantity,
      });

      console.log(`⚡ Instant craft completed ${craftId} x${quantity} at ${config.station}`);
      return true;
    }

    // Добавляем в активные крафты для обычных крафтов
    const now = Date.now();
    const craftTime = config.base_time_s * 1000; // Конвертируем в миллисекунды

    const newCraft: ActiveCraft = {
      craftId,
      startTime: now,
      endTime: now + craftTime,
      quantity,
      station: config.station,
    };

    const currentCrafts = this.activeCrafts.get();
    this.activeCrafts.set([...currentCrafts, newCraft]);

    // Отправляем событие о начале крафта
    gameEventBus.emit('craft-started', {
      craftId,
      quantity,
    });

    console.log(`🔨 Started crafting ${craftId} x${quantity} at ${config.station}`);
    return true;
  }

  /**
   * Получить все доступные крафты
   */
  getAvailableCrafts(): CraftInfo[] {
    const allCraftIds = this.ids() as CraftId[];
    const available: CraftInfo[] = [];

    for (const craftId of allCraftIds) {
      const info = this.getCraftInfo(craftId);
      if (info && info.isUnlocked) {
        available.push(info);
      }
    }

    return available.sort((a, b) => a.cost.requiredTier - b.cost.requiredTier);
  }

  /**
   * Получить активные крафты
   */
  getActiveCrafts(): ActiveCraft[] {
    return this.activeCrafts.get();
  }

  /**
   * Readable store for active crafts (for Svelte reactive subscriptions)
   */
  activeCraftsStore() {
    return { subscribe: this.activeCrafts.subscribe };
  }

  /**
   * Получить прогресс крафта (0-1)
   */
  getCraftProgress(craftId: CraftId): number {
    const activeCrafts = this.activeCrafts.get();
    const craft = activeCrafts.find(c => c.craftId === craftId);

    if (!craft) return 0;

    const now = Date.now();
    const totalTime = craft.endTime - craft.startTime;
    const elapsed = now - craft.startTime;

    return Math.min(Math.max(elapsed / totalTime, 0), 1);
  }

  /**
   * Получить оставшееся время крафта в секундах
   */
  getCraftRemainingTime(craftId: CraftId): number {
    const activeCrafts = this.activeCrafts.get();
    const craft = activeCrafts.find(c => c.craftId === craftId);

    if (!craft) return 0;

    const now = Date.now();
    const remaining = craft.endTime - now;

    return Math.max(Math.ceil(remaining / 1000), 0);
  }

  /**
   * Получить крафты по станции
   */
  getCraftsByStation(station: string): CraftInfo[] {
    return this.getAvailableCrafts().filter(craft => craft.cost.station === station);
  }

  /**
   * Получить все ID крафтов
   */
  getCraftIds(): CraftId[] {
    return this.ids() as CraftId[];
  }

  /**
   * Подписаться на изменения активных крафтов
   */
  subscribeToActiveCrafts(callback: (crafts: readonly ActiveCraft[]) => void): () => void {
    return this.activeCrafts.subscribe(callback);
  }

  /**
   * Подписаться на изменения уровня игрока
   */
  subscribeToPlayerLevel(callback: (level: number) => void): () => void {
    return this.playerLevel.subscribe(callback);
  }
}

// Ленивый синглтон для удобства
let _craftsInstance: CraftDB | null = null;
export function getCrafts(): CraftDB {
  if (!_craftsInstance) {
    _craftsInstance = new CraftDB();
  }
  return _craftsInstance;
}

// Синглтон для удобства
export const Crafts = new Proxy({} as CraftDB, {
  get(_target, prop) {
    return getCrafts()[prop as keyof CraftDB];
  },
});
